<script lang="ts">
import {Vue, Component} from 'vue-facing-decorator';

import { usePostHog } from '@/composables/usePostHog';

@Component({})
export default class SkillsComponent extends Vue {
  private posthog = usePostHog().posthog;
  private hoverTimer: number | undefined;
  private trackedSkills = new Set<string>();

  public onMouseEnter(skillId: string) {
    if (this.trackedSkills.has(skillId)) return;
    
    this.hoverTimer = window.setTimeout(() => {
      this.posthog.capture('skill_card_hovered', { skill_name: skillId });
      this.trackedSkills.add(skillId);
    }, 1500); // 1.5 seconds for intent
  }

  public onMouseLeave() {
    if (this.hoverTimer) {
      window.clearTimeout(this.hoverTimer);
      this.hoverTimer = undefined;
    }
  }
}
</script>
<template>
  <div class="global">
    <section class="skills-section">
      <div class="header-container">
        <h2>{{ $t('skills.title') }}</h2>
        <p class="subtitle">{{ $t('skills.subtitle') }}</p>
      </div>

      <div class="competencies-grid">
        <!-- Compétence 1 -->
        <div class="comp-card ln-glass-teal card-border-teal"
             @mouseenter="onMouseEnter('comp1')" @mouseleave="onMouseLeave">
          <div class="card-header">
            <span class="mdi mdi-code-braces card-icon"></span>
            <h3>{{ $t('skills.comp1.title') }}</h3>
          </div>
          <p class="projects-tag"><strong>{{ $t('skills.comp1.projects') }}</strong> {{ $t('skills.comp1.projectsList') }}</p>
          <ul class="feature-list">
            <li>{{ $t('skills.comp1.li1') }}</li>
            <li>{{ $t('skills.comp1.li2') }}</li>
            <li>{{ $t('skills.comp1.li3') }}</li>
            <li>{{ $t('skills.comp1.li4') }}</li>
            <li>{{ $t('skills.comp1.li5') }}</li>
          </ul>
        </div>

        <!-- Compétence 2 -->
        <div class="comp-card ln-glass-terracotta card-border-terracotta"
             @mouseenter="onMouseEnter('comp2')" @mouseleave="onMouseLeave">
          <div class="card-header">
            <span class="mdi mdi-lightning-bolt-outline card-icon"></span>
            <h3>{{ $t('skills.comp2.title') }}</h3>
          </div>
          <p class="projects-tag"><strong>{{ $t('skills.comp2.projects') }}</strong> {{ $t('skills.comp2.projectsList') }}</p>
          <ul class="feature-list">
            <li>{{ $t('skills.comp2.li1') }}</li>
            <li>{{ $t('skills.comp2.li2') }}</li>
            <li>{{ $t('skills.comp2.li3') }}</li>
            <li>{{ $t('skills.comp2.li4') }}</li>
            <li>{{ $t('skills.comp2.li5') }}</li>
          </ul>
        </div>

        <!-- Compétence 3 -->
        <div class="comp-card ln-glass-stone card-border-stone"
             @mouseenter="onMouseEnter('comp3')" @mouseleave="onMouseLeave">
          <div class="card-header">
            <span class="mdi mdi-server-network card-icon"></span>
            <h3>{{ $t('skills.comp3.title') }}</h3>
          </div>
          <p class="projects-tag"><strong>{{ $t('skills.comp3.projects') }}</strong> {{ $t('skills.comp3.projectsList') }}</p>
          <ul class="feature-list">
            <li>{{ $t('skills.comp3.li1') }}</li>
            <li>{{ $t('skills.comp3.li2') }}</li>
            <li>{{ $t('skills.comp3.li3') }}</li>
            <li>{{ $t('skills.comp3.li4') }}</li>
            <li>{{ $t('skills.comp3.li5') }}</li>
          </ul>
        </div>

        <!-- Compétence 4 -->
        <div class="comp-card ln-glass-beige card-border-beige"
             @mouseenter="onMouseEnter('comp4')" @mouseleave="onMouseLeave">
          <div class="card-header">
            <span class="mdi mdi-database card-icon"></span>
            <h3>{{ $t('skills.comp4.title') }}</h3>
          </div>
          <p class="projects-tag"><strong>{{ $t('skills.comp4.projects') }}</strong> {{ $t('skills.comp4.projectsList') }}</p>
          <ul class="feature-list">
            <li>{{ $t('skills.comp4.li1') }}</li>
            <li>{{ $t('skills.comp4.li2') }}</li>
            <li>{{ $t('skills.comp4.li3') }}</li>
            <li>{{ $t('skills.comp4.li4') }}</li>
            <li>{{ $t('skills.comp4.li5') }}</li>
          </ul>
        </div>

        <!-- Compétence 5 -->
        <div class="comp-card ln-glass-teal card-border-teal"
             @mouseenter="onMouseEnter('comp5')" @mouseleave="onMouseLeave">
          <div class="card-header">
            <span class="mdi mdi-clipboard-text-play-outline card-icon"></span>
            <h3>{{ $t('skills.comp5.title') }}</h3>
          </div>
          <p class="projects-tag"><strong>{{ $t('skills.comp5.projects') }}</strong> {{ $t('skills.comp5.projectsList') }}</p>
          <ul class="feature-list">
            <li>{{ $t('skills.comp5.li1') }}</li>
            <li>{{ $t('skills.comp5.li2') }}</li>
            <li>{{ $t('skills.comp5.li3') }}</li>
            <li>{{ $t('skills.comp5.li4') }}</li>
          </ul>
        </div>

        <!-- Compétence 6 -->
        <div class="comp-card ln-glass-terracotta card-border-terracotta"
             @mouseenter="onMouseEnter('comp6')" @mouseleave="onMouseLeave">
          <div class="card-header">
            <span class="mdi mdi-account-group-outline card-icon"></span>
            <h3>{{ $t('skills.comp6.title') }}</h3>
          </div>
          <p class="projects-tag"><strong>{{ $t('skills.comp6.projects') }}</strong> {{ $t('skills.comp6.projectsList') }}</p>
          <ul class="feature-list">
            <li>{{ $t('skills.comp6.li1') }}</li>
            <li>{{ $t('skills.comp6.li2') }}</li>
            <li>{{ $t('skills.comp6.li3') }}</li>
          </ul>
        </div>
      </div>
    </section>
  </div>
</template>

<style scoped>
.global {
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 3rem 2rem 6rem;
  min-height: calc(100vh - var(--ln-nav-offset));
}

.skills-section {
  width: 100%;
  max-width: var(--ln-content-max-width);
}

.header-container {
  text-align: center;
  margin-bottom: 4rem;
}

.header-container h2 {
  color: var(--ln-text-primary);
  font-size: clamp(2.5rem, 5vw, 3.5rem);
  font-weight: 800;
  margin-bottom: 1rem;
  position: relative;
  display: inline-block;
  padding-bottom: 0.5rem;
}

.header-container h2::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 100px;
  height: 4px;
  background: linear-gradient(to right, var(--ln-teal-deep), var(--ln-terracotta-subtle));
  border-radius: 2px;
}

.subtitle {
  font-size: 1.2rem;
  color: var(--ln-text-secondary);
}

.competencies-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2.5rem;
}

.comp-card {
  padding: 2.5rem;
  border-radius: var(--ln-radius-lg);
  border: 1px solid var(--ln-glass-border);
  transition: all var(--ln-transition-normal) var(--ln-ease-smooth);
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border-left: 6px solid var(--ln-teal-deep);
}

.comp-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 20px 40px var(--ln-glass-shadow-elevated);
}

.card-border-teal { border-left-color: var(--ln-teal-deep); }
.card-border-terracotta { border-left-color: var(--ln-terracotta-subtle); }
.card-border-stone { border-left-color: var(--ln-stone-grey); }
.card-border-beige { border-left-color: var(--ln-beige-sable); }

.card-header {
  display: flex;
  align-items: center;
  gap: 1.2rem;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--ln-glass-border);
}

.card-icon {
  font-size: 2.5rem;
  color: var(--ln-teal-deep);
  background: var(--ln-glass-surface);
  padding: 0.8rem;
  border-radius: 12px;
  box-shadow: inset 0 2px 4px var(--ln-glass-shadow);
}

.comp-card h3 {
  color: var(--ln-text-primary);
  font-size: 1.3rem;
  font-weight: 700;
  margin: 0;
  line-height: 1.3;
}

.ln-glass-terracotta .card-icon { color: var(--ln-terracotta-subtle); }
.ln-glass-stone .card-icon { color: var(--ln-stone-grey); }
.ln-glass-beige .card-icon { color: var(--ln-beige-sable); }

.projects-tag {
  font-size: 0.9rem;
  color: var(--ln-text-secondary);
  background: var(--ln-glass-base);
  padding: 0.8rem 1.2rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
}

.projects-tag strong {
  color: var(--ln-text-primary);
  font-weight: 700;
  margin-right: 0.5rem;
}

.feature-list {
  padding-left: 0;
  margin: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
}

.feature-list li {
  font-size: 1rem;
  color: var(--ln-text-secondary);
  padding-left: 1.8rem;
  position: relative;
  line-height: 1.6;
}

.feature-list li::before {
  content: '→';
  position: absolute;
  left: 0;
  font-weight: bold;
  color: var(--ln-teal-deep);
}

.ln-glass-terracotta .feature-list li::before { color: var(--ln-terracotta-subtle); }
.ln-glass-stone .feature-list li::before { color: var(--ln-stone-grey); }
.ln-glass-beige .feature-list li::before { color: var(--ln-beige-sable); }

/* Animation au chargement */
.comp-card {
  animation: glassFadeIn 0.6s var(--ln-ease-smooth) backwards;
}

.comp-card:nth-child(2) { animation-delay: 0.1s; }
.comp-card:nth-child(3) { animation-delay: 0.2s; }
.comp-card:nth-child(4) { animation-delay: 0.3s; }
.comp-card:nth-child(5) { animation-delay: 0.4s; }
.comp-card:nth-child(6) { animation-delay: 0.5s; }

/* Responsive */
@media (max-width: 768px) {
  .global {
    padding: 2rem 1rem;
  }

  .header-container {
    margin-bottom: 2rem;
  }

  .competencies-grid {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }

  .comp-card {
    padding: 1.5rem;
  }
}
</style>
